import axios, { AxiosError } from 'axios';
import { getAbsenceAPIUrl } from '../../../services/env.service';
import {
  ConfirmReplacementRequest,
  CreateAbsenceRequest,
  CreateReplacementRequest,
  GetAbsenceResponse,
  GetAllDeployabilityOptionsResponse,
  GetConfirmationsResponse,
  GetDriverDeployabilityResponse,
  GetReplacersResponse,
  GetSoonestAllowedAbsenceDateResponse,
  UpdateAbsenceRequest
} from './types';

const absenceUrl = getAbsenceAPIUrl();

class AbsenceClient {
  getAbsence(driverId: number) {
    return new Promise<GetAbsenceResponse>((resolve, reject) => {
      axios
        .get<GetAbsenceResponse>(`${absenceUrl}/driver/${driverId}/absence`)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  createAbsence(driverId: number, payload: CreateAbsenceRequest) {
    return new Promise<void>((resolve, reject) => {
      axios
        .post(`${absenceUrl}/driver/${driverId}/absence`, payload)
        .then(() => resolve())
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  updateAbsence(driverId: number, absenceId: number, payload: UpdateAbsenceRequest) {
    return new Promise<void>((resolve, reject) => {
      axios
        .put(`${absenceUrl}/driver/${driverId}/absence/${absenceId}`, payload)
        .then(() => resolve())
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  getPossibleReplacers(driverId: number) {
    return new Promise<GetReplacersResponse>((resolve, reject) => {
      axios
        .get<GetReplacersResponse>(`${absenceUrl}/driver/${driverId}/replacer`)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  getSoonestAllowedAbsenceDate(driverId: number) {
    return new Promise<GetSoonestAllowedAbsenceDateResponse>((resolve, reject) => {
      axios
        .get<GetSoonestAllowedAbsenceDateResponse>(`${absenceUrl}/driver/${driverId}/absence/date`)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  createReplacement(driverId: number, absencePeriodId: number, payload: CreateReplacementRequest) {
    return new Promise<void>((resolve, reject) => {
      axios
        .post(`${absenceUrl}/driver/${driverId}/absence/${absencePeriodId}/replacement`, payload)
        .then(() => resolve())
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  removeReplacement(driverId: number, absenceId: number, replacementId: number) {
    return new Promise<void>((resolve, reject) => {
      axios
        .delete(
          `${absenceUrl}/driver/${driverId}/absence/${absenceId}/replacement/${replacementId}`
        )
        .then(() => resolve())
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  getConfirmations(driverId: number) {
    return new Promise<GetConfirmationsResponse>((resolve, reject) => {
      axios
        .get<GetConfirmationsResponse>(`${absenceUrl}/driver/${driverId}/confirmation`)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  confirmConfirmation(
    driverId: number,
    absencePeriodId: number,
    replacementId: number,
    payload: ConfirmReplacementRequest
  ) {
    return new Promise<GetConfirmationsResponse>((resolve, reject) => {
      axios
        .put(
          `${absenceUrl}/driver/${driverId}/absence/${absencePeriodId}/replacement/${replacementId}/status`,
          payload
        )
        .then((response) => resolve(response.data))
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  getAllDeployabilityOptions() {
    return new Promise<GetAllDeployabilityOptionsResponse>((resolve, reject) => {
      axios
        .get<GetAllDeployabilityOptionsResponse>(`${absenceUrl}/driver/deployability/all-options`)
        .then((response) => resolve(response.data))
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  getDriverDeployability(driverId: number) {
    return new Promise<GetDriverDeployabilityResponse>((resolve, reject) => {
      axios
        .get<GetDriverDeployabilityResponse>(`${absenceUrl}/driver/${driverId}/deployability`)
        .then((response) => resolve(response.data))
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  createDriverDeployabilityOption(driverId: number, deployabilityOptionId: number) {
    return new Promise<boolean>((resolve, reject) => {
      axios
        .post<boolean>(`${absenceUrl}/driver/${driverId}/deployability`, {
          driverId,
          deployabilityOptionId
        })
        .then((response) => resolve(response.data))
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  removeDriverDeployabilityOption(driverId: number, driverDeployabilityOptionId: number) {
    return new Promise<void>((resolve, reject) => {
      axios
        .delete(`${absenceUrl}/driver/${driverId}/deployability/${driverDeployabilityOptionId}`)
        .then((response) => resolve())
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  createDriverDeployabilityDayNote(driverId: number, deployabilityDayId: number, note: string) {
    return new Promise<boolean>((resolve, reject) => {
      axios
        .post<boolean>(`${absenceUrl}/driver/${driverId}/deployability-day-note`, {
          deployabilityDayId,
          note
        })
        .then((response) => resolve(response.data))
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  updateDriverDeployabilityDayNote(
    driverId: number,
    driverDeployabilityDayNoteId: number,
    note: string
  ) {
    return new Promise<boolean>((resolve, reject) => {
      axios
        .put<boolean>(
          `${absenceUrl}/driver/${driverId}/deployability-day-note/${driverDeployabilityDayNoteId}`,
          { note }
        )
        .then((response) => resolve(response.data))
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  removeDriverDeployabilityDayNote(driverId: number, driverDeployabilityDayNoteId: number) {
    return new Promise<void>((resolve, reject) => {
      axios
        .delete(
          `${absenceUrl}/driver/${driverId}/deployability-day-note/${driverDeployabilityDayNoteId}`
        )
        .then((response) => resolve())
        .catch((error) => {
          const axiosError = error as AxiosError;
          const errorMessage = this.getErrorMessage(axiosError);
          reject(new Error(errorMessage));
        });
    });
  }

  getErrorMessage(error: AxiosError) {
    const status = (error && error.response && error.response.status) || null;

    let message = 'Er is een onbekende fout opgetreden';

    if (status === null) {
      message = 'Logistics is tijdelijk onbereikbaar. Probeer het later opnieuw.';
    } else if (status === 401 || status === 403) {
      message =
        'U heeft geen rechten om deze actie uit te voeren. Neem contact op met de klantenservice.';
    } else if (status === 400 && error.response && error.response.data.detail) {
      message = error.response.data.detail;
    }

    return message;
  }
}

const absenceClient = new AbsenceClient();

export { absenceClient };
